// ------------------------------------------------------------
// AI Attribution — per Loren Data AI Use Policy §8.2
// Tool:        Claude Code (Anthropic)
// 2026-07-15: Added convertToContract action orchestrating ConvertToContractFECommand (WI9896 Phase 4) - SangPham
// ------------------------------------------------------------
/**
 * useDeveloperPlan Hook
 * Orchestrates data fetching and extend trial via mediator
 */

import { useCallback, useEffect } from 'react';
import { logger } from '@/application/core';
import {
  GetDeveloperPlanTenantsQuery,
  ExtendDeveloperTrialFECommand,
  SyncDeveloperNetworksFECommand,
  ConvertToContractFECommand,
  mediator,
  type DeveloperPlanTenantsResponse,
  type ExtendTrialResult,
  type ConvertToContractResult,
} from '@/application';
import {
  GetRegistrationSessionsQuery,
  type RegistrationSessionsResponse,
} from '@/application/queries/registrationSessions/GetRegistrationSessionsQuery';
import {
  RetryRegistrationFECommand,
  type RetryRegistrationResult,
} from '@/application/commands/retryRegistration/RetryRegistrationCommand';
import { useDeveloperPlanStore } from './DeveloperPlanPage.store';
import { useRegistrationSessionsStore } from './registration-sessions.store';

/**
 * Hook return type
 */
interface UseDeveloperPlanResult {
  data: DeveloperPlanTenantsResponse | null;
  loading: boolean;
  error: Error | null;
  extending: boolean;
  extendError: Error | null;
  extendSuccess: boolean;
  syncing: boolean;
  syncError: Error | null;
  converting: boolean;
  convertError: Error | null;
  convertSuccess: boolean;
  refetch: () => Promise<void>;
  extendTrial: (networkId: number, newTrialEndDate: string) => Promise<void>;
  syncData: () => Promise<void>;
  convertToContract: (networkId: number) => Promise<void>;
}

/**
 * Hook to fetch developer plan tenants and extend trials
 * Uses mediator.send() for CQRS pattern
 */
export function useDeveloperPlan(): UseDeveloperPlanResult {
  const data = useDeveloperPlanStore((s) => s.data);
  const loading = useDeveloperPlanStore((s) => s.loading);
  const error = useDeveloperPlanStore((s) => s.error);
  const extending = useDeveloperPlanStore((s) => s.extending);
  const extendError = useDeveloperPlanStore((s) => s.extendError);
  const extendSuccess = useDeveloperPlanStore((s) => s.extendSuccess);
  const syncing = useDeveloperPlanStore((s) => s.syncing);
  const syncError = useDeveloperPlanStore((s) => s.syncError);
  const converting = useDeveloperPlanStore((s) => s.converting);
  const convertError = useDeveloperPlanStore((s) => s.convertError);
  const convertSuccess = useDeveloperPlanStore((s) => s.convertSuccess);
  const setData = useDeveloperPlanStore((s) => s.setData);
  const setLoading = useDeveloperPlanStore((s) => s.setLoading);
  const setError = useDeveloperPlanStore((s) => s.setError);
  const setExtending = useDeveloperPlanStore((s) => s.setExtending);
  const setExtendError = useDeveloperPlanStore((s) => s.setExtendError);
  const setExtendSuccess = useDeveloperPlanStore((s) => s.setExtendSuccess);
  const setSyncing = useDeveloperPlanStore((s) => s.setSyncing);
  const setSyncError = useDeveloperPlanStore((s) => s.setSyncError);
  const setConverting = useDeveloperPlanStore((s) => s.setConverting);
  const setConvertError = useDeveloperPlanStore((s) => s.setConvertError);
  const setConvertSuccess = useDeveloperPlanStore((s) => s.setConvertSuccess);
  const removeTenant = useDeveloperPlanStore((s) => s.removeTenant);
  const updateTenantTrialDate = useDeveloperPlanStore((s) => s.updateTenantTrialDate);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const result = await mediator.send<DeveloperPlanTenantsResponse>(
        new GetDeveloperPlanTenantsQuery()
      );
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch developer plan tenants');
      setError(error);
      logger.error('useDeveloperPlan', 'Error:', err);
    }
  }, [setData, setLoading, setError]);

  const extendTrial = useCallback(
    async (networkId: number, newTrialEndDate: string) => {
      setExtendError(null);
      setExtendSuccess(false);
      setExtending(true);

      try {
        const result = await mediator.send<ExtendTrialResult>(
          new ExtendDeveloperTrialFECommand(networkId, newTrialEndDate)
        );
        setExtendSuccess(true);
        // Update only the affected tenant row in-place (no full page reload)
        updateTenantTrialDate(result.networkId, result.newTrialEndDate);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to extend trial');
        setExtendError(error);
        logger.error('useDeveloperPlan', 'ExtendTrial Error:', err);
      }
    },
    [setExtending, setExtendError, setExtendSuccess, updateTenantTrialDate]
  );

  const syncData = useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const result = await mediator.send<DeveloperPlanTenantsResponse>(
        new SyncDeveloperNetworksFECommand()
      );
      setData(result);
    } catch (err) {
      const syncErr = err instanceof Error ? err : new Error('Failed to sync data');
      setSyncError(syncErr);
      logger.error('useDeveloperPlan', 'Sync Error:', err);
    } finally {
      setSyncing(false);
    }
  }, [setSyncing, setSyncError, setData]);

  const convertToContract = useCallback(
    async (networkId: number) => {
      setConvertError(null);
      setConvertSuccess(false);
      setConverting(true);

      try {
        await mediator.send<ConvertToContractResult>(new ConvertToContractFECommand(networkId));
        setConvertSuccess(true);
        // Graduated network drops off the Developer Plan list — remove in-place
        removeTenant(networkId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to convert network to contract');
        setConvertError(error);
        logger.error('useDeveloperPlan', 'ConvertToContract Error:', err);
      }
    },
    [setConverting, setConvertError, setConvertSuccess, removeTenant]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    extending,
    extendError,
    extendSuccess,
    syncing,
    syncError,
    converting,
    convertError,
    convertSuccess,
    refetch: fetchData,
    extendTrial,
    syncData,
    convertToContract,
  };
}

/**
 * Hook for registration sessions management
 * Uses Zustand store with individual selectors
 */
export function useRegistrationSessions() {
  const sessions = useRegistrationSessionsStore((s) => s.sessions);
  const sessionsLoading = useRegistrationSessionsStore((s) => s.sessionsLoading);
  const sessionsError = useRegistrationSessionsStore((s) => s.sessionsError);
  const retrying = useRegistrationSessionsStore((s) => s.retrying);
  const setSessions = useRegistrationSessionsStore((s) => s.setSessions);
  const setSessionsLoading = useRegistrationSessionsStore((s) => s.setSessionsLoading);
  const setSessionsError = useRegistrationSessionsStore((s) => s.setSessionsError);
  const setRetrying = useRegistrationSessionsStore((s) => s.setRetrying);

  const fetchSessions = useCallback(async () => {
    setSessionsError(null);
    setSessionsLoading(true);

    try {
      const result = await mediator.send<RegistrationSessionsResponse>(
        new GetRegistrationSessionsQuery({ pageSize: 100 })
      );
      setSessions(result.sessions);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch registration sessions');
      setSessionsError(error);
      setSessionsLoading(false);
      logger.error('useRegistrationSessions', 'Error:', err);
    }
  }, [setSessions, setSessionsLoading, setSessionsError]);

  const retryRegistration = useCallback(
    async (sessionId: string, sessionType: string, notes: string) => {
      setRetrying(true);

      try {
        await mediator.send<RetryRegistrationResult>(
          new RetryRegistrationFECommand(
            sessionId,
            sessionType as 'registration' | 'upgrade',
            notes
          )
        );
        // Refresh list after successful retry
        await fetchSessions();
      } catch (err) {
        logger.error('useRegistrationSessions', 'Retry Error:', err);
        throw err; // Re-throw for dialog to handle
      } finally {
        setRetrying(false);
      }
    },
    [fetchSessions, setRetrying]
  );

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    sessions,
    sessionsLoading,
    sessionsError,
    failedCount: sessions.filter(s => s.status === 'failed').length,
    refetchSessions: fetchSessions,
    retryRegistration,
    retrying,
  };
}
